import React, {useMemo} from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {Button, Modal, Form, Input} from 'components/antd';
import {
  ProspectTag,
  useUpdateProspectTagMutation,
  useDestroyProspectTagMutation,
  usePoolQuery,
} from 'api';
import {CheckIcon} from '@heroicons/react/20/solid';
import {tagColors} from 'helpers/tagColors';

interface Props {
  prospectTag: ProspectTag;
  onClose: () => void;
}

export default ({prospectTag, onClose}: Props) => {
  const {data: {pool: {prospectTags = []} = {}} = {}} = usePoolQuery();

  const [updateProspectTag] = useUpdateProspectTagMutation({
    refetchQueries: ['pool'],
    onCompleted: () => onClose(),
  });

  const [destroyProspectTag] = useDestroyProspectTagMutation({
    refetchQueries: ['pool'],
    onCompleted: onClose,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: prospectTag?.name,
      color: prospectTag?.color,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('必須項目です'),
    }),
    onSubmit: (values) =>
      updateProspectTag({
        variables: {
          uuid: prospectTag.uuid,
          attributes: {
            ...values,
            poolId: prospectTag.poolId,
          },
        },
      }),
  });

  const alreadyNameExist = useMemo(
    () => prospectTags?.some((tag) => tag.name === formik.values.name),
    [formik.values.name, prospectTags],
  );

  return (
    <Modal
      destroyOnClose
      visible={Boolean(prospectTag)}
      width={480}
      centered
      transitionName=""
      maskTransitionName=""
      footer={null}
      onCancel={onClose}>
      <ModalContent>
        <h2 className="pb-0">タグを編集</h2>
        <Form layout="vertical" onFinish={formik.handleSubmit}>
          <Form.Item label="タグ名" required>
            <Input
              size="large"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            {alreadyNameExist && formik.values.name !== prospectTag?.name && (
              <span className="text-red-500 mt-2">
                既に同じ名前のタグが存在します
              </span>
            )}
          </Form.Item>
          <Form.Item label="タグの色を選択">
            {tagColors.map((color, index) => (
              <div
                key={index}
                className="w-[34px] h-[34px] rounded-md cursor-pointer inline-block mr-4 mb-2 relative"
                style={{
                  backgroundColor: color.hex,
                }}
                onClick={() => {
                  formik.setFieldValue('color', color.hex);
                }}>
                {formik.values.color === color.hex && (
                  <CheckIcon className="w-6 h-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                )}
              </div>
            ))}
          </Form.Item>
          <Actions>
            <Button size="large" onClick={onClose}>
              キャンセル
            </Button>
            <div>
              <Button
                danger
                onClick={() => {
                  if (confirm('削除してよろしいですか？')) {
                    destroyProspectTag({
                      variables: {
                        uuid: prospectTag.uuid,
                      },
                    });
                  }
                }}
                size="large">
                削除
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{marginLeft: '8px'}}
                disabled={!formik.isValid || !formik.dirty}>
                保存
              </Button>
            </div>
          </Actions>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  h2 {
    font-size: 18px;
    font-weight: 700;
  }

  form {
    margin-top: 1rem;
  }

  label {
    font-weight: bold;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;
